import React from "react";

import Image from "next/image";
import Link from "next/link";

import { EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Column, ORDERING, Record } from "@gsmvalve/gsmvalve-table";
import { t, Trans } from "@lingui/macro";
import { Button, Row, Tag } from "antd";
import { keyBy } from "lodash";
import get from "lodash/get";

import AppLayout from "layout/app";

import EmptyStates from "components/empty-states";
import { ActionMenu } from "components/navigations/header";
import Table, { onHasFilters, pageCountMessage, TableSearch } from "components/table";

import { useApplicationContext } from "contexts/app";

import { IFilter } from "@gsmvalve/gsmvalve-table/dist/cjs/types";
import { Driver, DriverGroup } from "types";

const limit = 100;
const refreshInterval = 15000;
const excludedDriverGroup = ["default"];

const modifier = (accountURL: string | undefined) => (data: any) => {
  const records: Record[] = [];

  data?.forEach((rows: any) => {
    const drivers = rows?.data.drivers;
    const driverGroups = rows?.data?.driver_groups.filter(
      (group: DriverGroup) => !excludedDriverGroup.includes(group?.group_type as any) && group.account === accountURL
    );

    const keyedDriverGroups: { [key: string]: DriverGroup } = keyBy(driverGroups, "url");

    drivers?.forEach((driver: any) => {
      records.push({
        ...driver,
        driver_group_names: driver?.driver_groups
          ?.map((group: string) => keyedDriverGroups?.[group]?.name_display)
          .filter(Boolean)
          .join(", "),
      });
    });
  });

  return records;
};

const filters: IFilter[] = [
  { key: "account", omit: true, title: "" },
  { key: "driver_group", omit: true, title: "" },
  { key: "driver_groups", omit: true, title: "" },
  { key: "permissions", omit: true, title: "" },
  { key: "user", omit: true, title: "" },
  { key: "managed_permissions", omit: true, title: "" },
  { key: "display", omit: true, title: "" },
  { key: "url", omit: true, title: "" },
  { key: "id", omit: true, title: "" },
];

const Drivers = () => {
  const { session, configuration } = useApplicationContext();
  const columns: Column[] = [
    {
      key: "name",
      title: t`Name`,
      isVisible: true,
      dataIndex: ["name"],
      fixed: true,
      isSortable: true,
      render: (data) => {
        if (!data?.name) {
          return;
        }

        return <Link href={`/settings/drivers/${data?.id}/?tab=overview`}>{data?.name}</Link>;
      },
    },
    {
      key: "email",
      title: t`Email`,
      isVisible: true,
      dataIndex: ["email"],
      isSortable: true,
    },
    {
      key: "phone",
      title: t`Phone`,
      isVisible: true,
      dataIndex: ["phone"],
      isSortable: true,
    },
    {
      key: "driver_groups",
      title: t`Driver Groups`,
      isVisible: true,
      dataIndex: ["driver_group_names"],
      isSortable: false,
    },
    {
      key: "active",
      width: 100,
      title: t`Status`,
      isVisible: true,
      dataIndex: ["active"],
      render: (data) => <Tag color={data.active ? "green" : undefined}>{data.active ? t`Active` : t`Inactive`}</Tag>,
      isSortable: true,
    },
    {
      key: "actions",
      width: 90,
      title: t`Actions`,
      render: (data) => {
        return (
          <Link
            href={`/settings/drivers/${data?.id}/?tab=overview&name=${data?.name}`}
            as={`/settings/drivers/${data?.id}/?tab=overview`}
          >
            <Button type={"text"} icon={<EditOutlined />} />
          </Link>
        );
      },
      isVisible: false,
    },
    { key: "external_id", title: t`External id` },
    { key: "eco_score_enabled", title: t`Eco score enabled` },
    { key: "account", omit: true },
    { key: "user", omit: true },
    { key: "url", omit: true },
  ];
  const account = get(configuration, "account.url");

  const url = `drivers/?account=${session?.accountId}&permissions=can_manage&count=true&active=True&sideload=driver_groups`;
  const newDriverButton = (
    <Link href={"/settings/drivers/new?tab=overview"}>
      <Button type={"primary"} icon={<PlusCircleOutlined />}>
        <span>
          <Trans>New Driver</Trans>
        </span>
      </Button>
    </Link>
  );

  return (
    <>
      <AppLayout pageTitle={t`Drivers`} fullscreen={true}>
        <ActionMenu
          menus={[
            <Row>
              <TableSearch placeholder={t`Search drivers...`} />
            </Row>,
            newDriverButton,
          ]}
        />
        <Table<Driver[]>
          key={"drivers"}
          id={`${session?.accountId}-drivers`}
          columns={{
            filtersPath: "/drivers/",
            path: "Driver",
            customColumns: columns,
            customFilters: filters,
          }}
          records={{
            url: url,
            sort: [{ key: "name", order: "asc" as ORDERING }],
            limit: limit,
            refreshInterval: refreshInterval,
            modifier: modifier(account),
          }}
          tabsFilter={{
            key: "active",
            default: "True",
            filters: [
              { value: "True", label: t`Active` },
              { value: "False", label: t`Inactive` },
              { value: "", label: t`All` },
            ],
          }}
          empty={{
            emptyWithoutFilter: (
              <EmptyStates
                title={t`No drivers yet.`}
                description={t`You can create a driver and then manage their permissions using a button below.`}
                icon={
                  <Image
                    src={"/images/drivers-empty-state.svg"}
                    width={260}
                    height={260}
                    alt={t`drivers empty state`}
                  />
                }
                actions={[newDriverButton]}
              />
            ),
            onHasFilter: onHasFilters(),
          }}
          controls={{
            manageColumn: true,
            tabSort: true,
            filterBtn: false,
            pageCountMessage: pageCountMessage(t`Drivers`),
          }}
        />
      </AppLayout>
    </>
  );
};

export default Drivers;
